import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { take, switchMap, takeUntil } from 'rxjs/operators';

import { AppState } from '../../../../../../state/app.state';
import { loadSidenavData, submitSearch } from '../../../../../../state/action';
import { selectSidenavDataByTabIdAndKey } from '../../../../../../state/selectors';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import { IThematicTechSummaryRow } from '../../../../../../core/models/results.model';
import { getStageColor, getStageBgColor, StageName } from '../../../../../../shared/utils/bracket.utils';
import { LoadingProgressComponent, LoadingStage } from '../../../../../../shared/components/loading-progress/loading-progress.component';

const SIDENAV_KEY = 'thematic_technology_summary';
const DESCRIPTION_KEY = 'thematic_cluster_description';
const PAGE_SIZE = 10;
const STAGE_ORDER: StageName[] = ['Emerging', 'Accelerating', 'Maturing', 'Declining'];

const LOADING_STAGES: LoadingStage[] = [
  { text: 'Scanning the theme’s patent footprint…', atSeconds: 0 },
  { text: 'Aggregating technology clusters…', atSeconds: 10 },
  { text: 'Ranking top technology areas…', atSeconds: 22 },
];

@Component({
  selector: 'app-thematic-technology-summary',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, LoadingProgressComponent],
  templateUrl: './thematic-technology-summary.component.html',
  styleUrl: './thematic-technology-summary.component.css',
})
export class ThematicTechnologySummaryComponent implements OnChanges, OnDestroy {
  @Input() tabId!: string;
  @Input() tab_name: string | null = null;
  @Input() query: string | null = null;
  @Input() theme_params: unknown = null;

  private destroy$ = new Subject<void>();

  allRows: IThematicTechSummaryRow[] = [];
  filteredRows: IThematicTechSummaryRow[] = [];
  pagedRows: IThematicTechSummaryRow[] = [];

  activeFilter: string = 'All Fields';
  filters: string[] = ['All Fields', ...STAGE_ORDER];

  currentPage = 0;
  pageSize = PAGE_SIZE;
  totalPages = 0;

  description: string | null = null;

  readonly LOADING_STAGES = LOADING_STAGES;

  showSpinner = false;
  showTable = false;
  loadingFailed = false;

  constructor(
    private store: Store<AppState>,
    private apiService: GeneralInnovationActivityResultsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabId'] || changes['query']) {
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private reset(): void {
    this.allRows = [];
    this.filteredRows = [];
    this.pagedRows = [];
    this.activeFilter = 'All Fields';
    this.currentPage = 0;
    this.description = null;
    this.showTable = false;
    this.loadingFailed = false;
  }

  private loadDescription(): void {
    const currentTabId = this.tabId;
    const p = (this.theme_params as any)?.params ?? {};
    if (p.theme_search_type === 'custom' || !p.cluster_id) return;

    this.store.select(selectSidenavDataByTabIdAndKey(currentTabId, DESCRIPTION_KEY)).pipe(
      take(1),
      switchMap((cached: string | null) => {
        if (cached != null) return of(cached);
        return this.apiService.getClusterMetadata({ cluster_id: String(p.cluster_id), tabId: currentTabId }).pipe(
          switchMap((res) => {
            const desc = res.body?.payload?.data?.description ?? '';
            this.store.dispatch(loadSidenavData({ tabId: currentTabId, sidenavKey: DESCRIPTION_KEY, data: desc }));
            return of(desc);
          }),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (desc) => {
        if (this.tabId === currentTabId) this.description = desc || null;
      },
    });
  }

  private loadData(): void {
    this.reset();
    this.loadDescription();
    this.showSpinner = true;
    const currentTabId = this.tabId;
    const p = (this.theme_params as any)?.params ?? {};

    this.store.select(selectSidenavDataByTabIdAndKey(currentTabId, SIDENAV_KEY)).pipe(
      take(1),
      switchMap((cached: IThematicTechSummaryRow[] | null) => {
        if (cached) {
          return of({ data: cached, targetTabId: currentTabId });
        }
        return this.apiService.getThematicTechnologySummary({
          cluster_id: p.cluster_id ?? null,
          cluster_name: p.cluster_name ?? this.tab_name ?? null,
          query: this.query ?? '',
          theme_search_type: p.theme_search_type ?? 'cluster',
          tabId: currentTabId,
        }).pipe(
          switchMap((res) => {
            const payload = res.body?.payload;
            const data = payload?.data ?? [];
            const targetTabId = payload?.tabId ?? currentTabId;
            this.store.dispatch(loadSidenavData({ tabId: targetTabId, sidenavKey: SIDENAV_KEY, data }));
            return of({ data, targetTabId });
          }),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: ({ data, targetTabId }) => {
        if (this.tabId !== currentTabId || targetTabId !== currentTabId) return;
        this.showSpinner = false;
        if (!data.length) {
          this.loadingFailed = true;
          return;
        }
        this.allRows = data;
        this.applyFilter(this.activeFilter);
        this.showTable = true;
      },
      error: () => {
        if (this.tabId === currentTabId) {
          this.showSpinner = false;
          this.loadingFailed = true;
        }
      },
    });
  }

  applyFilter(filter: string): void {
    this.activeFilter = filter;
    this.filteredRows = filter === 'All Fields'
      ? [...this.allRows]
      : this.allRows.filter(r => r.stage_name === filter);
    this.currentPage = 0;
    this.totalPages = Math.ceil(this.filteredRows.length / this.pageSize);
    this.updatePage();
  }

  updatePage(): void {
    const start = this.currentPage * this.pageSize;
    this.pagedRows = this.filteredRows.slice(start, start + this.pageSize);
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages) return;
    this.currentPage = page;
    this.updatePage();
  }

  getStageColor(stage: string): string {
    return getStageColor(stage);
  }

  getStageBgColor(stage: string): string {
    return getStageBgColor(stage);
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i);
  }

  submitTheme(row: IThematicTechSummaryRow): void {
    const searchParams = {
      themes: [{
        api_query: null,
        name: row.cluster_name,
        theme_search_type: 'cluster',
        cluster_id: String(row.cluster_id),
        cluster_name: row.cluster_name,
      }]
    };
    this.store.dispatch(submitSearch({ searchParams, searchType: 'theme' }));
  }
}
